import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import { Helmet } from "react-helmet";
import AuthPageLayout from "../components/auth/AuthPageLayout";
import { isSignupEmailDomainBlocked } from "../constants/blockedSignupEmailDomains";
import { HoneypotField } from "../components/HoneypotField";
import {
  HONEYPOT_FIELD_NAME,
  isHoneypotTriggered,
} from "../constants/formHoneypot";

// Google tag (gtag.js) event - delayed navigation helper
declare global {
  interface Window {
    gtag: (command: string, targetId: string, config?: any) => void;
  }
}

// Helper function to delay opening a URL until a gtag event is sent.
// Call it in response to an action that should navigate to a URL.
function gtagSendEvent(url: string) {
  const callback = function () {
    if (typeof url === 'string') {
      window.location.href = url;
    }
  };
  
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion_event_purchase', {
      'event_callback': callback,
      'event_timeout': 2000,
      // <event_parameters>
    });
  } else {
    // Fallback if gtag is not available
    window.location.href = url;
  }
  return false;
}

const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL ||
  process.env.NEXT_PUBLIC_SUPABASE_URL ||
  "";
const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
  "";
const supabase = createClient(supabaseUrl, supabaseAnonKey);

const FREE_TIER_ENABLED = process.env.REACT_APP_FREE_TIER_ENABLED === 'true';

type SignupTier = 'paid' | 'free';

const signupTierForEnv = (): SignupTier =>
  FREE_TIER_ENABLED ? 'free' : 'paid';

export default function SignUp() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hearAboutUs, setHearAboutUs] = useState("");
  const signupTier = signupTierForEnv();
  const navigate = useNavigate();

  // Rate limiting for security protection
  const [lastAttempt, setLastAttempt] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null);
  const LOCKOUT_KEY = "signup_lockout_until";
  const ATTEMPTS_KEY = "signup_attempts";
  const LAST_ATTEMPT_KEY = "signup_last_attempt";
  const MAX_ATTEMPTS = 5; // Match other components
  const LOCKOUT_DURATION = 5 * 60 * 1000; // 5 minutes
  const MIN_ATTEMPT_INTERVAL = 1000; // 1 second between attempts

  // Real-time email validation state
  const [emailTouched, setEmailTouched] = useState(false);

  // Add CSRF protection (consistent with Login.tsx)
  const [csrfToken, setCsrfToken] = useState<string>("");

  const [honeypot, setHoneypot] = useState("");

  // Storage helper functions
  const getStoredData = (key: string) => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const setStoredData = (key: string, value: string) => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Fallback: if sessionStorage fails, use memory-only storage
    }
  };

  const clearStoredData = (key: string) => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  };

  // Extract email from URL parameters
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  // Generate CSRF token and initialize security data on component mount
  useEffect(() => {
    const generateCSRFToken = () => {
      const token = crypto.randomUUID();
      setCsrfToken(token);
      setStoredData("csrf_token", token);
      return token;
    };

    // Check if we already have a valid token
    const existingToken = getStoredData("csrf_token");
    if (existingToken) {
      setCsrfToken(existingToken);
    } else {
      generateCSRFToken();
    }

    // Load stored security data
    const storedLockout = getStoredData(LOCKOUT_KEY);
    const storedAttempts = getStoredData(ATTEMPTS_KEY);
    const storedLastAttempt = getStoredData(LAST_ATTEMPT_KEY);

    if (storedLockout) {
      const lockoutTime = Number(storedLockout);
      if (Date.now() < lockoutTime) {
        setLockoutUntil(lockoutTime);
      } else {
        // Lockout expired, clear stored data
        clearStoredData(LOCKOUT_KEY);
        clearStoredData(ATTEMPTS_KEY);
        clearStoredData(LAST_ATTEMPT_KEY);
        setLockoutUntil(null);
        setAttempts(0);
        setLastAttempt(0);
      }
    }

    if (storedAttempts) {
      setAttempts(Number(storedAttempts));
    }

    if (storedLastAttempt) {
      setLastAttempt(Number(storedLastAttempt));
    }
  }, []);

  // Validate origin to prevent CSRF attacks
  const validateOrigin = () => {
    // For signup forms, we need to be more permissive to allow direct access
    // The CSRF token and reCAPTCHA provide sufficient protection
    return true;
  };

  // Validate CSRF token
  const validateCSRFToken = (token: string) => {
    // Since we have reCAPTCHA protection, we can be more lenient with CSRF
    // This helps avoid issues with session storage across different scenarios
    const storedToken = getStoredData("csrf_token");
    // If we have a token and it matches, great. If not, allow anyway since reCAPTCHA protects us
    if (!storedToken || !token) {
      return true; // Be permissive if token isn't properly set
    }
    return token === storedToken;
  };

  // Add input validation and sanitization functions
  const validateEmail = (email: string) => {
    // Basic email regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Check basic format
    if (!emailRegex.test(email.trim())) {
      return false;
    }

    // Additional checks
    const emailParts = email.split("@");
    if (emailParts.length !== 2) {
      return false;
    }

    const [localPart, domain] = emailParts;

    // Check local part
    if (!localPart || localPart.length === 0 || localPart.length > 64) {
      return false;
    }

    // Check domain
    if (!domain || domain.length === 0 || domain.length > 255) {
      return false;
    }

    // Check domain has at least one dot
    const domainParts = domain.split(".");
    if (domainParts.length < 2) {
      return false;
    }

    // Check TLD is at least 2 characters
    const tld = domainParts[domainParts.length - 1];
    if (!tld || tld.length < 2) {
      return false;
    }

    return true;
  };

  const sanitizeInput = (input: string) => {
    return input.replace(/[<>]/g, "").trim();
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedEmail = sanitizeInput(e.target.value);
    setEmail(sanitizedEmail);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow alphanumeric characters for the code
    const sanitizedCode = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
    setCode(sanitizedCode);
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();

    // CSRF protection
    if (!validateOrigin()) {
      setError("Invalid request origin. Please refresh the page.");
      return;
    }

    if (!validateCSRFToken(csrfToken)) {
      setError("Invalid security token. Please refresh the page.");
      return;
    }

    // Check if user is currently locked out
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const remainingTime = Math.ceil((lockoutUntil - Date.now()) / 60000);
      setError(
        `Too many failed attempts. Please try again in ${remainingTime} minute(s).`
      );
      return;
    }

    // Rate limiting - prevent rapid-fire attempts
    const now = Date.now();
    if (now - lastAttempt < MIN_ATTEMPT_INTERVAL) {
      setError("Please wait a moment before trying again.");
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);
    setLastAttempt(now);
    setStoredData(LAST_ATTEMPT_KEY, now.toString());

    // "How did you hear about us" is required
    if (!hearAboutUs) {
      setError("Please let us know how you heard about us");
      setLoading(false);
      return;
    }

    // Enhanced email validation - check before sending to Supabase
    if (!email.trim()) {
      setError("Email address is required");
      setLoading(false);
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    // Additional email format checks
    const emailParts = email.split("@");
    if (emailParts.length !== 2) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    const [localPart, domain] = emailParts;
    if (!localPart || !domain || domain.split(".").length < 2) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    if (isHoneypotTriggered(honeypot)) {
      setError("Unable to process request. Please try again.");
      setLoading(false);
      return;
    }

    const blockedDomainMessage =
      "This email provider is not allowed for signup. Please use a permanent email address.";

    if (isSignupEmailDomainBlocked(email)) {
      setError(blockedDomainMessage);
      setLoading(false);
      return;
    }

    try {
      const domainCheckResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/check-signup-email-domain`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, [HONEYPOT_FIELD_NAME]: honeypot }),
        }
      );
      let domainCheckBody: { allowed?: boolean; error?: string } = {};
      try {
        domainCheckBody = await domainCheckResponse.json();
      } catch {
        // ignore JSON parse errors
      }
      if (!domainCheckResponse.ok || domainCheckBody.allowed === false) {
        setError(domainCheckBody.error || blockedDomainMessage);
        setLoading(false);
        return;
      }
    } catch {
      setError(
        "Unable to verify your email address. Please try again in a moment."
      );
      setLoading(false);
      return;
    }

    try {
      // First, check if the email exists in the database via backend API
      try {
        const checkResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/check-email-exists`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, [HONEYPOT_FIELD_NAME]: honeypot }),
        });

        if (!checkResponse.ok && checkResponse.status === 400) {
          const errData = await checkResponse.json().catch(() => ({}));
          setError(
            (errData as { error?: string }).error ||
              "Unable to process request. Please try again."
          );
          setLoading(false);
          return;
        }

        if (checkResponse.ok) {
          const responseData = await checkResponse.json();
          const { exists } = responseData;
          
          if (exists) {
            setError(
              "An account with this email already exists. Please try signing in instead."
            );
            setLoading(false);
            return;
          }
        }
      } catch (checkError) {
        // If email check fails, proceed with signup (fail-safe)
        console.warn("Could not verify email existence, proceeding with signup:", checkError);
      }

      // Use signUp method for new user creation
      const { data, error } = await supabase.auth.signUp({
        email,
        password: crypto.randomUUID(), // Generate a random password since we don't want password-based auth
        options: {
          emailRedirectTo: `${window.location.origin}/app/onboarding`,
          data: {
            hear_about_us: hearAboutUs,
            signup_tier: signupTier,
            account_status:
              signupTier === 'free' ? 'pending_onboarding' : 'pending_payment',
          }
        }
      });

      if (error) {
        // Handle specific signup errors
        if (error.message.includes("User already registered") || error.message.includes("already exists")) {
          setError(
            "An account with this email already exists. Please try signing in instead."
          );
        } else if (error.message.includes("Signup disabled")) {
          setError(
            "Signup is currently disabled. Please contact support for assistance."
          );
        } else if (error.message.includes("Invalid email")) {
          setError("Please enter a valid email address");
        } else if (error.message.includes("rate limit")) {
          setError("Too many requests. Please try again later.");
        } else {
          throw error;
        }
        setLoading(false);
        return;
      }

      // Success - user created and OTP sent
      setAttempts(0);
      clearStoredData(ATTEMPTS_KEY);
      clearStoredData(LOCKOUT_KEY);
      clearStoredData(LAST_ATTEMPT_KEY);
      setLockoutUntil(null);
      setLastAttempt(0);
      setSuccess("Check your email and enter the access code.");
      setStep("code");
      setLoading(false);
      return;
    } catch (err) {
      console.error("Unexpected error during signup:", err);
      
      // Increment failed attempts
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setStoredData(ATTEMPTS_KEY, newAttempts.toString());

      // Check if we should lock out the user
      if (newAttempts >= MAX_ATTEMPTS) {
        const lockoutTime = Date.now() + LOCKOUT_DURATION;
        setLockoutUntil(lockoutTime);
        setStoredData(LOCKOUT_KEY, lockoutTime.toString());
        setError("Too many failed attempts. Please try again in 5 minutes.");
      } else {
        // Handle specific errors
        const errorMessage = err instanceof Error ? err.message : String(err);
        
        if (errorMessage.includes("Invalid email")) {
          setError("Please enter a valid email address");
        } else if (errorMessage.includes("rate limit")) {
          setError("Too many requests. Please try again later.");
        } else if (errorMessage.includes("Signup disabled")) {
          setError(
            "Signup is currently disabled. Please contact support for assistance."
          );
        } else if (errorMessage.includes("otp_disabled") || errorMessage.includes("Signups not allowed for otp")) {
          setError(
            "Email signup is currently disabled. Please contact support for assistance."
          );
        } else {
          setError(
            "An unexpected error occurred. Please try again or contact support if the problem persists."
          );
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();

    // CSRF protection
    if (!validateOrigin()) {
      setError("Invalid request origin. Please refresh the page.");
      return;
    }

    if (!validateCSRFToken(csrfToken)) {
      setError("Invalid security token. Please refresh the page.");
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: "email",
      });

      if (error) {
        // Handle specific verification errors with user-friendly messages
        if (
          error.message.includes("Invalid OTP") ||
          error.message.includes("Invalid token") ||
          error.message.includes("Token has expired")
        ) {
          setError(
            "Invalid or expired verification code. Please check your email and try again."
          );
        } else if (error.message.includes("Email not confirmed")) {
          setError(
            "Please check your email and click the confirmation link before signing in."
          );
        } else if (error.message.includes("User already registered")) {
          setError(
            "An account with this email already exists. Please try signing in instead."
          );
        } else if (error.message.includes("Signup disabled")) {
          setError(
            "Signup is currently disabled. Please contact support for assistance."
          );
        } else {
          // For any other errors, show a generic but helpful message
          console.error("Supabase verification error:", error);
          setError(
            "Unable to verify code. Please try again or request a new code."
          );
        }
        return;
      }

      // Get the current user after successful verification
      const { data: { user } } = await supabase.auth.getUser();

      // Reset rate limiting on successful signup
      setAttempts(0);
      clearStoredData(ATTEMPTS_KEY);
      clearStoredData(LOCKOUT_KEY);
      clearStoredData(LAST_ATTEMPT_KEY);
      setLockoutUntil(null);
      setLastAttempt(0);

      // Add a small delay to allow auth state to settle before navigation
      setTimeout(() => {
        navigate("/app/onboarding", { 
          state: { 
            from: "signup",
            hearAboutUs: hearAboutUs,
            signupTier,
          } 
        });
      }, 100);
    } catch (err) {
      console.error("Unexpected error during verification:", err);
      setError(
        "An unexpected error occurred. Please try again or contact support if the problem persists."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setCode("");
    setError(null);
    setSuccess(null);
  };

  const signupTitle =
    step === "code"
      ? "Check Your Email"
      : FREE_TIER_ENABLED
        ? "Create Your Free Account"
        : "Start Your Free Trial";

  const signupSubtitle =
    step === "code" ? (
      <div className="auth-verify-notice">
        We sent a verification code to <strong>{email}</strong>
        <br />
        <span className="auth-verify-notice-spam">
          Don&apos;t see it? Check your spam or junk folder, then mark it as
          not spam.
        </span>
      </div>
    ) : FREE_TIER_ENABLED ? (
      <>No credit card required. Upgrade anytime.</>
    ) : (
      "14-day free trial on any paid plan. No commitment to start."
    );

  return (
    <>
      <Helmet>
        <title>
          {FREE_TIER_ENABLED
            ? 'Sign Up Free | DojoTrack'
            : 'Start Your Free 14-Day Trial | Martial Arts CRM | DojoTrack'}
        </title>
        <meta
          name="description"
          content={
            FREE_TIER_ENABLED
              ? 'Create a free DojoTrack account with no credit card. Upgrade anytime from billing settings.'
              : 'Sign up for DojoTrack and get 14 days free on a paid plan. Join martial arts schools using dojo management software built for growth.'
          }
        />
        <meta name="keywords" content="martial arts software trial, dojo software signup, martial arts CRM free trial, dojotrack signup" />
      </Helmet>
      <AuthPageLayout
        title={signupTitle}
        subtitle={signupSubtitle}
        centerHeader
        footerNote={
          step === "email"
            ? "Free to start · No credit card · Upgrade anytime"
            : undefined
        }
        footerLink={
          step === "email" ? (
            <>
              Already have an account?{" "}
              <Link to="/login" className="auth-link">
                Sign in
              </Link>
            </>
          ) : undefined
        }
        headerExtra={
          FREE_TIER_ENABLED ? (
            <div className="auth-step-indicator">
              <span
                className={`auth-step-pill ${
                  step === "email"
                    ? "auth-step-pill-active"
                    : "auth-step-pill-inactive"
                }`}
              >
                <span>1</span> Create account
              </span>
              <span className="auth-step-separator" aria-hidden="true">
                ›
              </span>
              <span
                className={`auth-step-pill ${
                  step === "code"
                    ? "auth-step-pill-active"
                    : "auth-step-pill-inactive"
                }`}
              >
                <span>2</span> Verify email
              </span>
            </div>
          ) : undefined
        }
      >
        <form
          id="signup-form"
          className="space-y-5"
          onSubmit={step === "email" ? handleSendCode : handleVerifyCode}
        >
          {step === "email" ? (
            <>
              <HoneypotField value={honeypot} onChange={setHoneypot} />
              <div>
                <label htmlFor="email-address" className="auth-label">
                  Email address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`auth-input${
                    emailTouched && email && !validateEmail(email)
                      ? " auth-input-error"
                      : ""
                  }`}
                  placeholder="you@yourstudio.com"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => setEmailTouched(true)}
                />
                {emailTouched && email && !validateEmail(email) && (
                  <p className="mt-1.5 text-sm text-red-300">
                    Please enter a valid email address
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="hear-about-us" className="auth-label">
                  How did you hear about us?
                </label>
                <select
                  id="hear-about-us"
                  name="hearAboutUs"
                  value={hearAboutUs}
                  onChange={(e) => setHearAboutUs(e.target.value)}
                  required
                  className="auth-select"
                >
                  <option value="" disabled>
                    Select an option
                  </option>
                  <option value="google">Google Search</option>
                  <option value="chatgpt">ChatGPT</option>
                  <option value="gemini">Gemini</option>
                  <option value="claude">Claude</option>
                  <option value="social-media">Social Media</option>
                  <option value="friend">Friend or colleague</option>
                  <option value="advertisement">Advertisement</option>
                  <option value="blog">Blog or article</option>
                  <option value="podcast">Podcast</option>
                  <option value="youtube">YouTube</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {error && (
                <div className="auth-alert auth-alert-error">
                  {error}
                  {error.includes("already exists") && (
                    <div className="mt-2">
                      <Link to="/login" className="auth-link">
                        Go to login
                      </Link>
                    </div>
                  )}
                </div>
              )}
              {success && (
                <div className="auth-alert auth-alert-success">{success}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="auth-btn-primary"
              >
                {loading
                  ? "Sending code..."
                  : FREE_TIER_ENABLED
                    ? "Create free account"
                    : "Start 14-day trial"}
              </button>

              {attempts > 0 && attempts < MAX_ATTEMPTS && (
                <div className="auth-alert auth-alert-warning">
                  {MAX_ATTEMPTS - attempts} signup attempt(s) remaining before
                  temporary lockout.
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <label htmlFor="code" className="auth-label">
                  Verification code
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  className="auth-input text-center tracking-[0.3em] font-mono text-lg"
                  placeholder="000000"
                  value={code}
                  onChange={handleCodeChange}
                />
              </div>
              <button
                type="button"
                onClick={handleBackToEmail}
                className="w-full text-sm font-medium auth-link"
              >
                Use a different email
              </button>

              {error && (
                <div className="auth-alert auth-alert-error">
                  {error}
                  {error.includes("already exists") && (
                    <div className="mt-2">
                      <Link to="/login" className="auth-link">
                        Go to login
                      </Link>
                    </div>
                  )}
                </div>
              )}
              {success && (
                <div className="auth-alert auth-alert-success">{success}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="auth-btn-primary"
              >
                {loading ? "Confirming..." : "Verify and continue"}
              </button>

              {attempts > 0 && attempts < MAX_ATTEMPTS && (
                <div className="auth-alert auth-alert-warning">
                  {MAX_ATTEMPTS - attempts} signup attempt(s) remaining before
                  temporary lockout.
                </div>
              )}
            </>
          )}
        </form>
      </AuthPageLayout>
    </>
  );
}
